import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  useActivateEconTemplateMutation,
  useLazyDownloadEconTemplateQuery,
  useListEconTemplatesQuery,
  useUploadEconTemplateMutation,
} from '@shared/api';
import type { EconTemplate } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { IconCheck, IconDownload, IconFile } from '@shared/ui/icons';
import {
  ECON_MODULE_IDS,
  ECON_MODULES,
  apiErrorMessage,
  isEconModuleId,
  useEconPermission,
  type EconModuleId,
} from '@entities/economic/types';
import { useActionPermission } from '@entities/user';

/** По активному шаблону собираются все отчёты модуля — активация требует
 *  права-действия `econ.bdr.approve` (ТЗ п. 6.10, Д8). */
const TEMPLATE_APPROVAL_HINT =
  'Активирует сотрудник с правом утверждать БДР: главный экономист или руководитель';

const Layout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const FormTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 15px;
  font-weight: 600;
`;

const FileHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  & input {
    width: 16px;
    height: 16px;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }
`;

const ContractArea = styled.textarea<{ $invalid: boolean }>`
  width: 100%;
  min-height: 96px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.colors.status.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12.5px;
  line-height: 1.5;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
    font-family: ${({ theme }) => theme.font.family};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, $invalid }) =>
      $invalid ? theme.colors.status.danger : theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Ok = styled.div`
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

// Свёрнутый блок «Расширенно»: ручной JSON-контракт нужен лишь специалистам
// (для bdr карта строится автоматически), поэтому по умолчанию скрыт.
const Advanced = styled.details`
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  & > summary {
    cursor: pointer;
    font-size: 12.5px;
    color: ${({ theme }) => theme.colors.text.secondary};
    user-select: none;
    list-style: revert;
    padding: ${({ theme }) => `${theme.spacing(1)} 0`};
  }

  & > summary:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// Результат автопостроения карты — заметный, но не тревожный блок.
const AutoMap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AutoMapTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Warn = styled.div`
  color: ${({ theme }) => theme.colors.status.warning};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

const UnmatchedList = styled.ul`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(4)};
  max-height: 140px;
  overflow-y: auto;
  line-height: 1.5;

  & > li {
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const ListHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const FilterTab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? 'currentColor' : theme.colors.border)};
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const GroupLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const TemplateRow = styled(Card)`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Version = styled.div`
  font-weight: 700;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  width: 44px;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
  min-width: 160px;
  overflow: hidden;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const RowDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
`;

const Empty = styled(Card)`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
  padding: ${({ theme }) => theme.spacing(8)} ${({ theme }) => theme.spacing(4)};
`;

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Версии — по убыванию: свежая сверху. */
function byVersionDesc(a: EconTemplate, b: EconTemplate): number {
  return b.version - a.version;
}

/** Склонение «договор» по числу: 1 договор / 2 договора / 5 договоров. */
function pluralizeContracts(n: number): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'договоров';
  if (mod10 === 1) return 'договор';
  if (mod10 >= 2 && mod10 <= 4) return 'договора';
  return 'договоров';
}

function TemplateItem({
  tpl,
  canActivate,
  activateHint,
  onActivate,
  onDownload,
  activating,
  downloading,
}: {
  tpl: EconTemplate;
  canActivate: boolean;
  /** Почему нельзя активировать (видно в подсказке кнопки). */
  activateHint?: string;
  onActivate: (id: string) => void;
  onDownload: (tpl: EconTemplate) => void;
  activating: boolean;
  downloading: boolean;
}) {
  return (
    <TemplateRow $accent={tpl.is_active}>
      <Version>v{tpl.version}</Version>
      <FileName>
        <IconFile size={15} />
        <span title={tpl.original_filename}>{tpl.original_filename}</span>
      </FileName>
      {tpl.is_active ? (
        <Badge $tone="success">активный</Badge>
      ) : (
        <Badge $tone="neutral">архив</Badge>
      )}
      <RowDate>{fmtDateTime(tpl.created_at)}</RowDate>
      <RowActions>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => onDownload(tpl)}
          disabled={downloading}
        >
          <IconDownload size={15} />
          Скачать
        </Button>
        {!tpl.is_active && (
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => onActivate(tpl.id)}
            disabled={!canActivate || activating}
            title={activateHint}
          >
            <IconCheck size={15} />
            Активировать
          </Button>
        )}
      </RowActions>
    </TemplateRow>
  );
}

export function TemplatesView() {
  const { can_write } = useEconPermission();
  const approval = useActionPermission('econ.bdr.approve');
  const canActivate = can_write && approval.allowed;
  const activateHint = !can_write
    ? 'Только просмотр'
    : !approval.allowed
      ? TEMPLATE_APPROVAL_HINT
      : undefined;

  // Загрузка нового шаблона.
  const [module, setModule] = useState<EconModuleId>(ECON_MODULE_IDS[0]);
  const [file, setFile] = useState<File | null>(null);
  const [contract, setContract] = useState('');
  const [activate, setActivate] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Результат автопостроения карты привязки (для bdr без ручного JSON).
  const [autoMap, setAutoMap] = useState<{
    matched: number;
    unmatched: string[];
  } | null>(null);

  // Фильтр списка по модулю (null — все).
  const [filter, setFilter] = useState<EconModuleId | null>(null);

  const listQ = useListEconTemplatesQuery(
    filter ? { module: filter } : {},
  );
  const [upload, { isLoading: uploading }] = useUploadEconTemplateMutation();
  const [activateTemplate, { isLoading: activating }] = useActivateEconTemplateMutation();
  const [downloadTemplate, { isFetching: downloading }] = useLazyDownloadEconTemplateQuery();

  // Группировка версий по модулю для читаемого реестра.
  const grouped = useMemo(() => {
    const items = listQ.data?.items ?? [];
    const map = new Map<string, EconTemplate[]>();
    for (const t of items) {
      const list = map.get(t.module) ?? [];
      list.push(t);
      map.set(t.module, list);
    }
    for (const list of map.values()) list.sort(byVersionDesc);
    return map;
  }, [listQ.data]);

  // Проверка валидности sheet_contract до отправки — пустое поле допустимо.
  const contractInvalid = useMemo(() => {
    const trimmed = contract.trim();
    if (!trimmed) return false;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return typeof parsed !== 'object' || parsed === null || Array.isArray(parsed);
    } catch {
      return true;
    }
  }, [contract]);

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
    setSuccess(null);
    setAutoMap(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setAutoMap(null);
    if (!file) {
      setError('Приложите файл шаблона (.xlsx).');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setError('Файл шаблона должен быть .xlsx.');
      return;
    }
    if (contractInvalid) {
      setError('Контракт листов должен быть валидным JSON-объектом.');
      return;
    }

    const fd = new FormData();
    fd.append('module', module);
    fd.append('file', file);
    const trimmedContract = contract.trim();
    if (trimmedContract) fd.append('sheet_contract', trimmedContract);
    // Без права активации версия загружается в архив: бэкенд иначе ответит 403.
    if (activate && canActivate) fd.append('activate', 'true');

    try {
      const res = await upload(fd).unwrap();
      setFile(null);
      setContract('');
      setSuccess(
        `Загружена версия v${res.version} модуля «${
          isEconModuleId(res.module) ? ECON_MODULES[res.module].short : res.module
        }»${res.is_active ? ' (активна)' : ''}.`,
      );
      // Карта построена автоматически (bdr без ручного JSON) — показываем итог.
      if (res.auto_mapped) {
        setAutoMap({ matched: res.matched_count, unmatched: res.unmatched });
      }
    } catch (err) {
      // Backend отдаёт понятный русский текст при структурной проблеме шаблона
      // (нет листа «Приложение № 1», нет формул SUMIFS). Формат ошибки API —
      // `{ error: { code, message } }` (см. osnova-api/src/error.rs).
      const data =
        typeof err === 'object' && err !== null && 'data' in err
          ? (err as { data?: { error?: { message?: string } } }).data
          : undefined;
      const message = data?.error?.message;
      setError(
        message ?? 'Не удалось загрузить шаблон. Проверьте файл и попробуйте снова.',
      );
      // eslint-disable-next-line no-console
      console.error('uploadEconTemplate failed', err);
    }
  }

  async function handleActivate(id: string) {
    setError(null);
    try {
      await activateTemplate(id).unwrap();
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось активировать версию шаблона.');
      // eslint-disable-next-line no-console
      console.error('activateEconTemplate failed', err);
    }
  }

  async function handleDownload(tpl: EconTemplate) {
    setError(null);
    try {
      const blob = await downloadTemplate(tpl.id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = tpl.original_filename || `econ_template_${tpl.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Не удалось скачать шаблон.');
      // eslint-disable-next-line no-console
      console.error('downloadEconTemplate failed', err);
    }
  }

  // Модули для рендера реестра: при активном фильтре — только он.
  const renderModules = filter ? [filter] : ECON_MODULE_IDS;
  const total = listQ.data?.items.length ?? 0;

  return (
    <Layout>
      <Card>
        <FormTitle>Новый шаблон отчёта</FormTitle>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="tpl-module">Модуль</Label>
            <Select
              id="tpl-module"
              value={module}
              onChange={(e) => setModule(e.target.value as EconModuleId)}
            >
              {ECON_MODULE_IDS.map((id) => (
                <option key={id} value={id}>
                  {ECON_MODULES[id].title}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="tpl-file">Файл шаблона (.xlsx)</Label>
            <Input id="tpl-file" type="file" accept=".xlsx" onChange={onFile} />
            <FileHint>
              Excel-шаблон, по которому собирается итоговый отчёт обработки.
              {module === 'bdr'
                ? ' Карта привязки строится автоматически из самого шаблона.'
                : ''}
              {file ? ` · ${file.name}` : ''}
            </FileHint>
          </Field>

          <Advanced>
            <summary>Расширенно (заполняется автоматически)</summary>
            <Field>
              <Label htmlFor="tpl-contract">Контракт листов (JSON, необязательно)</Label>
              <ContractArea
                id="tpl-contract"
                $invalid={contractInvalid}
                placeholder='{"version": 1, "sheets": [ ... ]}'
                value={contract}
                onChange={(e) => setContract(e.target.value)}
                spellCheck={false}
                aria-invalid={contractInvalid}
              />
              <FileHint>
                {contractInvalid
                  ? 'Сейчас это не валидный JSON-объект — исправьте или очистите поле.'
                  : 'Для bdr оставьте пустым — карта построится автоматически. ' +
                    'Заполните вручную только чтобы переопределить раскладку.'}
              </FileHint>
            </Field>
          </Advanced>

          <CheckRow>
            <input
              type="checkbox"
              checked={activate && canActivate}
              disabled={!canActivate}
              onChange={(e) => setActivate(e.target.checked)}
            />
            Сделать активным сразу после загрузки
          </CheckRow>
          {can_write && !approval.allowed && (
            <FileHint>{TEMPLATE_APPROVAL_HINT}. Загруженная версия останется в архиве.</FileHint>
          )}

          {error && <Err>{error}</Err>}
          {success && <Ok>{success}</Ok>}

          {autoMap && (
            <AutoMap>
              <AutoMapTitle>
                Карта построена автоматически: {autoMap.matched}{' '}
                {pluralizeContracts(autoMap.matched)}
              </AutoMapTitle>
              {autoMap.unmatched.length > 0 ? (
                <>
                  <Warn>
                    Без строки-итога в реестрах: {autoMap.unmatched.length}{' '}
                    {pluralizeContracts(autoMap.unmatched.length)}. Их выручка не
                    попадёт в отчёт, пока строки не появятся в КС-2/КС-3.
                  </Warn>
                  <UnmatchedList>
                    {autoMap.unmatched.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </UnmatchedList>
                </>
              ) : (
                <div>Все договоры «Приложения №1» привязаны.</div>
              )}
            </AutoMap>
          )}

          <Button
            type="submit"
            $variant="primary"
            disabled={uploading || !can_write || contractInvalid}
            title={!can_write ? 'Только просмотр' : undefined}
          >
            {uploading ? 'Загрузка…' : 'Загрузить версию'}
          </Button>
        </form>
      </Card>

      <Stack>
        <Filters>
          <FilterTab
            type="button"
            $active={filter === null}
            aria-pressed={filter === null}
            onClick={() => setFilter(null)}
          >
            Все модули
          </FilterTab>
          {ECON_MODULE_IDS.map((id) => (
            <FilterTab
              key={id}
              type="button"
              $active={filter === id}
              aria-pressed={filter === id}
              onClick={() => setFilter(id)}
            >
              {ECON_MODULES[id].short}
            </FilterTab>
          ))}
        </Filters>

        <ListHead>
          <Muted>Версий: {total}</Muted>
          {listQ.isFetching && <Muted>обновление…</Muted>}
        </ListHead>

        {total === 0 && !listQ.isLoading ? (
          <Empty>
            Шаблоны ещё не загружены. Приложите .xlsx-шаблон слева — он станет основой для сборки
            отчётов обработок выбранного модуля.
          </Empty>
        ) : (
          renderModules.map((id) => {
            const items = grouped.get(id);
            if (!items || items.length === 0) return null;
            return (
              <Group key={id}>
                <GroupLabel>{ECON_MODULES[id].title}</GroupLabel>
                {items.map((tpl) => (
                  <TemplateItem
                    key={tpl.id}
                    tpl={tpl}
                    canActivate={canActivate}
                    activateHint={activateHint}
                    onActivate={handleActivate}
                    onDownload={handleDownload}
                    activating={activating}
                    downloading={downloading}
                  />
                ))}
              </Group>
            );
          })
        )}
      </Stack>
    </Layout>
  );
}
